import { useState, useEffect } from 'react'
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import type { AdminUser, UserFormData, UpdateUserFormData, CreateUserResponse, UpdateUserResponse, DeleteUserResponse } from '@/types/admin'
import { useToast } from '@/hooks/use-toast'

/**
 * Custom hook for managing users in the admin dashboard
 * Provides functions for CRUD operations on users
 */
export function useAdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Fetch all users with their profiles from Edge Function
  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      // Call Edge Function that uses service role to fetch from auth.users
      // and joins with user_profiles for complete user data
      const { data, error: functionError } = await supabase.functions.invoke('admin-list-users')

      if (functionError) {
        throw functionError
      }

      if (!data || !data.success || !data.users) {
        throw new Error('Invalid response from admin-list-users function')
      }

      setUsers(data.users as AdminUser[])
    } catch (err) {
      console.error('fetchUsers error:', err)
      const message = (err as Error)?.message || 'Failed to fetch users'
      setError(message)
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  // Create a new user
  const createUser = async (userData: UserFormData): Promise<CreateUserResponse | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-user', {
        body: userData,
      })

      if (error) {
        // Log full error for debugging
        console.error('Edge Function error:', error)
        
        // Extract error message following supabase-js v2 guidance
        let errorMessage = 'Failed to create user'
        let field: string | undefined
        try {
          if (error instanceof FunctionsHttpError && (error as any).context?.json) {
            const body = await (error as any).context.json()
            if (body?.error) errorMessage = typeof body.error === 'string' ? body.error : JSON.stringify(body.error)
            else if (body?.message) errorMessage = body.message
            if (typeof body?.field === 'string') field = body.field
          } else if (error instanceof FunctionsRelayError || error instanceof FunctionsFetchError) {
            errorMessage = error.message
          } else if ((error as any)?.message) {
            errorMessage = (error as any).message
          }
        } catch (parseErr) {
          // Fall back to generic message
          console.error('Failed to parse Edge Function error body:', parseErr)
        }
        if (field) errorMessage = `${errorMessage} (${field})`
        throw new Error(errorMessage)
      }

      toast({
        title: 'Success',
        description: `User ${userData.email} created successfully. Confirmation email sent.`,
      })

      // Refresh user list
      await fetchUsers()

      return data as CreateUserResponse
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to create user'
      console.error('Error creating user:', err)
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      return null
    }
  }

  // Update an existing user
  const updateUser = async (userId: string, userData: UpdateUserFormData): Promise<UpdateUserResponse | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-update-user', {
        body: { userId, ...userData },
      })

      if (error) throw error

      toast({
        title: 'Success',
        description: 'User updated successfully',
      })

      // Refresh user list
      await fetchUsers()

      return data as UpdateUserResponse
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to update user'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      return null
    }
  }

  // Delete a user
  const deleteUser = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: { userId },
      })

      if (error) throw error

      toast({
        title: 'Success',
        description: 'User deleted successfully',
      })

      // Refresh user list
      await fetchUsers()

      return true
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to delete user'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      return false
    }
  }

  // Fetch a single user by ID
  const fetchUser = async (userId: string): Promise<AdminUser | null> => {
    try {
      // Call Edge Function that uses service role to fetch from auth.users
      // This is more efficient than querying user_profiles directly since we need email from auth.users
      const { data, error } = await supabase.functions.invoke('admin-list-users')

      if (error) throw error

      if (!data || !data.success || !data.users) {
        throw new Error('Invalid response from admin-list-users function')
      }

      // Filter for the specific user from the complete list
      const user = (data.users as AdminUser[]).find((u: AdminUser) => u.id === userId)

      if (!user) {
        throw new Error('User not found')
      }

      return user
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to fetch user'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      })
      return null
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    fetchUser,
  }
}
